import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatVersionTimestamp } from '../../storyboard';
import { StudioSelect } from '../StoryboardComposer/StudioSelect';
import * as S from './VersionPicker.styles';

/** One entry of an ad's version history, as the picker shows it. */
export interface VersionPickerItem {
  /** The version's stable key (`versionKey` of its `created_at`). */
  key: string;
  /** `created_at` as epoch ms, for the label. */
  createdAt: number;
  /** Generated (a locked version is never edited in place). */
  locked?: boolean;
  /** A run is filling this version right now. */
  generating?: boolean;
  /** A check flagged what this version generated (e.g. a landing page hero the vision check did not pass): its label
   *  ends with "Needs review", the same words as a flagged banner size's badge. Leave it off when every flag the
   *  version had was cleared (a resolved one stores as ok), so it reads like any other version. */
  needsReview?: boolean;
  /** What the check found, already translated by the caller in its own words (one literal `t()` per reason, as the
   *  size grid's badge does): shown under the flagged version's label in the list. Only read with `needsReview`. */
  reviewNote?: string;
}

export interface VersionPickerProps {
  /** Newest first (the backend's order). */
  versions: VersionPickerItem[];
  value?: string;
  onChange: (key: string) => void;
  disabled?: boolean;
}

/**
 * The version selector of an ad composer's header (banner kits, mini-games): the studio's compact select, one option
 * per version. The newest generated version reads "Latest", a version being filled "Generating", one never
 * generated "Not generated"; the rest show their time, and a version a check flagged ends with "Needs review" with its
 * reason under it in the list when the caller gives one. Hidden until the ad has a version.
 */
export const VersionPicker = ({
  versions,
  value,
  onChange,
  disabled,
}: VersionPickerProps): React.ReactElement | null => {
  const { t } = useTranslation();
  if (versions.length === 0 || !value) return null;
  const latestLocked = versions.findIndex((v) => v.locked && !v.generating);
  const options = versions.map((v, i) => {
    const when = formatVersionTimestamp(v.createdAt);
    const label = v.generating
      ? t('Generating · {{when}}', { when })
      : !v.locked
        ? t('Not generated · {{when}}', { when })
        : i === latestLocked
          ? t('Latest · {{when}}', { when })
          : when;
    const flagged = Boolean(v.needsReview) && !v.generating;
    const note = flagged ? v.reviewNote?.trim() : undefined;
    return {
      value: v.key,
      label: flagged ? t('{{label}} · Needs review', { label }) : label,
      ...(note ? { description: <S.ReviewNote raw text={note} /> } : {}),
    };
  });
  return (
    <StudioSelect
      compact
      hideLabel
      mobileIcon="clock"
      label={t('Version')}
      value={value}
      onChange={onChange}
      options={options}
      disabled={disabled}
    />
  );
};

export default VersionPicker;
